import { BigNumber, ethers } from 'ethers';
import { JsonRpcSigner } from '@ethersproject/providers';
import { TransactionResponse } from '@ethersproject/abstract-provider';

import appConfig from 'config/appConfig';
import { ZERO_ADDRESS } from 'constants/blockchain';

export class TradingContract {
  contract: ethers.Contract;
  provider: ethers.providers.JsonRpcProvider = new ethers.providers.JsonRpcProvider(appConfig.rpcEndpoint);

  constructor(signer: JsonRpcSigner) {
    this.contract = new ethers.Contract(appConfig.contracts.Trading.address, appConfig.contracts.Trading.abi, signer);
  }

  async create(
    address: string,
    amount: BigNumber,
    price: BigNumber,
    bid: boolean,
    paymentToken = ZERO_ADDRESS,
  ): Promise<{ txHash: string; offerId: number }> {
    const options: { value?: BigNumber } = {};

    if (!bid) {
      options['value'] = price;
    }

    const tx = (await this.contract.createSellBuy(
      address,
      amount,
      price,
      bid,
      paymentToken,
      options,
    )) as TransactionResponse;

    const receipt = await tx.wait();

    const filteredLogs = receipt.logs.filter(
      (log: ethers.providers.Log) => log.address === appConfig.contracts.Trading.address,
    );

    const AbiCoder = new ethers.utils.AbiCoder();
    const decodedData = AbiCoder.decode(
      ['uint256', 'address', 'uint256', 'uint256', 'address', 'bool', 'address'],
      filteredLogs[0].data,
    );
    return { txHash: tx.hash, offerId: decodedData[0].toNumber() };
  }

  async execute(offerId: number, amount: BigNumber, options = {}) {
    return this.contract.acceptSellBuy(offerId, amount, options);
  }

  private async getOperatorRole() {
    return this.contract.OPERATOR_ROLE();
  }

  async grantOperatorRole(accountAddress: string) {
    const operatorRole = await this.getOperatorRole();

    return this.contract.grantRole(operatorRole, accountAddress);
  }

  async hasOperatorRole(accountAddress: string) {
    const operatorRole = await this.getOperatorRole();

    return this.contract.hasRole(operatorRole, accountAddress);
  }

  async revokeOperatorRole(accountAddress: string) {
    const operatorRole = await this.getOperatorRole();

    return this.contract.revokeRole(operatorRole, accountAddress);
  }

  async deleteSellBuy(offerId: number) {
    return this.contract.deleteSellBuy(offerId);
  }

  async batchDeleteSellBuy(offerIds: number[]): Promise<TransactionResponse> {
    return this.contract.batchDeleteSellBuy(offerIds);
  }
}
